import { IStairConfig, IStepGeometry } from '@stair-craft/common/util';

import { IPlanPoint } from '../shared-plan-geometry/plan-geometry';

/**
 * True if any corner of a rotated winder step (its actual polygon footprint, not the
 * axis-aligned box) falls outside the room. Uses a 1mm tolerance because ray-wall
 * trig lands boundary corners a few ULPs outside after the floating-point round-trip.
 * Extracted from `computeWarningStepIndices` to keep its complexity low.
 */
function winderStepOutOfBounds(
    corners: NonNullable<IStepGeometry['winderCorners']>,
    stairX: number,
    stairZ: number,
    config: IStairConfig
): boolean {
    const points: { x: number; z: number }[] = [
        corners.innerBack,
        corners.innerFront,
        corners.outerBack,
        corners.outerFront,
        ...(corners.outerMidPoints ?? []),
        ...(corners.innerMidPoints ?? []),
    ];
    const tol: number = 1;
    return points.some((p: { x: number; z: number }): boolean => {
        const wx: number = stairX + p.x;
        const wz: number = stairZ + p.z;
        return wx < -tol || wx > config.roomWidth + tol || wz < -tol || wz > config.roomLength + tol;
    });
}

/**
 * True if a step's footprint pokes outside the room. Winders test their rotated
 * polygon corners; axis-aligned / 180°-rotated steps test the (width × depth) box,
 * swapping the extents for the rotated case. Extracted from `computeWarningStepIndices`.
 */
function stepOutOfRoom(step: IStepGeometry, stairX: number, stairZ: number, config: IStairConfig): boolean {
    if (step.isWinder && step.winderCorners) {
        return winderStepOutOfBounds(step.winderCorners, stairX, stairZ, config);
    }
    const worldX: number = stairX + step.position.x;
    const worldZ: number = stairZ + step.position.z;
    const stepW: number = step.treadWidth;
    const stepD: number = step.treadDepth;
    if (step.rotation === 0) {
        return worldZ < 0 || worldZ + stepD > config.roomLength || worldX < 0 || worldX + stepW > config.roomWidth;
    }
    return worldX < 0 || worldX + stepD > config.roomWidth || worldZ < 0 || worldZ + stepW > config.roomLength;
}

// Indices of steps to highlight red: the treads with too little headroom (`lowHeadroom`),
// steps poking outside the room, or insufficient entry clearance before flight 1.
export function computeWarningStepIndices(
    allSteps: IStepGeometry[],
    config: IStairConfig,
    placement: IPlanPoint,
    treadDepth: number,
    lowHeadroom: readonly number[],
    flight1Count: number,
    sw: number
): number[] {
    const warningSet: Set<number> = new Set<number>(lowHeadroom);
    const lastStepIdx: number = allSteps.length - 1;

    // Stair world position
    const stairX: number = placement.x;
    const stairZ: number = placement.z;

    // Room bounds: check each step against room boundaries. Winders test their
    // polygon corners (their arbitrary rotation makes the axis-aligned box a false
    // positive that lit middle winders red); other steps test the width × depth box.
    for (let i: number = 0; i <= lastStepIdx; i++) {
        const step: IStepGeometry = allSteps[i];
        if (stepOutOfRoom(step, stairX, stairZ, config)) {
            warningSet.add(step.index);
        }
    }

    // Entry clearance: insufficient space to approach flight 1
    if (stairZ >= 0 && stairZ < sw) {
        const lastBadStep: number = Math.min(flight1Count - 1, Math.ceil((sw - stairZ) / treadDepth));
        for (let i: number = 0; i <= lastBadStep; i++) {
            warningSet.add(i);
        }
    }

    return [...warningSet];
}
